import {
  ActionIcon,
  Badge,
  Box,
  Center,
  Group,
  Pagination,
  Space,
  Stack,
  Text,
  Title,
} from "@mantine/core"
import { useDisclosure, usePagination } from "@mantine/hooks"
import { InformationRequestStatus } from "@tfg/db"
import {
  Button,
  CountryFlag,
  CopyButton,
  IconNavion,
  IconMultipleNavion,
  Loader,
  Select,
  TextInput,
  Tooltip,
  Card,
} from "@tfg/ui"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { ContactResponses } from "@tfg/types/endpoints/contacts"
import { SupplierResponses } from "@tfg/types/endpoints/supplier"
import { typeTextContent } from "@tfg/types/endpoints/traceability"

import { i18n } from "~/i18n.ts"
import { TransferProdSiteRequestModal } from "~/pages/SupplierPortal/TraceabilityRequests/components/TransferProdSiteRequestModal.tsx"
import { getCustomFieldParser } from "~/pages/SupplierPortal/TraceabilityRequests/components/getCustomFieldParser.ts"
import { useDebouncedStateWithCallback } from "~/pages/SupplierPortal/TraceabilityRequests/hooks/useDebouncedStateWithCallback.ts"
import {
  useListContactsQuery,
  useReopenProdsiteFieldRequestMutation,
  useSetProdsiteFieldValueMutation,
  useSubmitProdsiteFieldRequestMutation,
} from "~/pages/SupplierPortal/TraceabilityRequests/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"
import { COUNTRIES } from "~/utils/countries.ts"
import { getTranslation, useCurrentLanguage } from "~/utils/translations.ts"

interface Props {
  prodsiteRequests: SupplierResponses.ProdsitefieldRequest[]
}
const REQUESTS_PER_PAGE = 10

export const ProdsiteRequests = ({ prodsiteRequests }: Props) => {
  const { t } = useTranslation(["traceability"])
  const pagination = usePagination({
    total: Math.ceil(prodsiteRequests.length / REQUESTS_PER_PAGE),
  })

  const [opened, { close, open }] = useDisclosure(false)

  const [prodSiteFieldValueIds, setProdsiteFieldValueIds] = useState<number[]>(
    []
  )

  const { data: contacts } = useListContactsQuery()

  const openTransferModal = (values: number[]) => {
    open()
    setProdsiteFieldValueIds(values)
  }

  return (
    <Stack>
      <Group justify="right">
        <Pagination
          size="sm"
          value={pagination.active}
          onChange={pagination.setPage}
          total={Math.ceil(prodsiteRequests.length / REQUESTS_PER_PAGE)}
        />
      </Group>
      {prodsiteRequests.length === 0 ? (
        <Center pt="xl">
          <Title order={3}>{t("prodsiterequest.noRequests")}</Title>
        </Center>
      ) : (
        <Stack>
          {prodsiteRequests
            .slice(
              (pagination.active - 1) * REQUESTS_PER_PAGE,
              pagination.active * REQUESTS_PER_PAGE
            )
            ?.map((request) => (
              <ProdsiteFieldRequest
                key={request.id}
                request={request}
                openTransferModal={openTransferModal}
                contacts={contacts ?? []}
              />
            ))}
          <TransferProdSiteRequestModal
            opened={opened}
            onClose={close}
            prodSiteFieldValueIds={prodSiteFieldValueIds}
          />
        </Stack>
      )}
    </Stack>
  )
}

type ProdsiteFieldRequestProps = {
  request: SupplierResponses.ProdsitefieldRequest
  openTransferModal: (value: number[]) => void
  contacts: ContactResponses.Contact[]
}

function ProdsiteFieldRequest({
  request,
  openTransferModal,
  contacts,
}: ProdsiteFieldRequestProps) {
  const { t } = useTranslation(["traceability"])
  const { isAdmin, user } = useAuth()
  const { mutate: submit, isLoading } = useSubmitProdsiteFieldRequestMutation(
    request.id
  )
  const { mutate: reopen, isLoading: isReopening } =
    useReopenProdsiteFieldRequestMutation(request.id)

  const isValid = useMemo(
    () => isProdsiteFieldRequestValid(request, user?.id),
    [request, user]
  )
  const isSubmitted = useMemo(
    () => isProdsiteFieldRequestSubmitted(request, user?.id),
    [request, user]
  )

  return (
    <Card p="md">
      <Group justify="space-between" align="start">
        <Stack>
          <Stack gap={0}>
            <Text fz="md">
              {request.name}
              {" - "}
              <CountryFlag countryIso3166={request.countryIso3166} />{" "}
              {COUNTRIES[request.countryIso3166].countryName}
            </Text>
            <Text c="dimmed">{request.address}</Text>
          </Stack>
          {isAdmin ? (
            <Text c="violet">
              <CopyButton
                copyableText={request.id.toString()}
                admin={true}
                label="Production Site Id"
                size="xs"
              />
            </Text>
          ) : null}
          {!isSubmitted && (
            <Badge color={isValid ? "yellow" : "red"}>
              {isValid
                ? t("prodsiterequest.status.TOBESUBMITTED")
                : t("prodsiterequest.status.INVALID")}
            </Badge>
          )}
        </Stack>
        {!isSubmitted ? (
          <Group justify="start">
            <Button
              w="fit-content"
              variant="secondary"
              rightSection={
                <IconMultipleNavion
                  hovered={true}
                  fill="var(--mantine-color-green-6"
                />
              }
              onClick={() =>
                openTransferModal(
                  request.prodsiteFieldValues
                    .filter((x) => x.submittedById === user?.id)
                    .map((x) => x.id)
                )
              }
            >
              {t("prodsiterequest.actions.transferAll")}
            </Button>
            <Button
              loading={isLoading}
              disabled={!isValid}
              onClick={() => submit()}
            >
              {t("certificates.actions.submit")}
            </Button>
          </Group>
        ) : (
          isAdmin && (
            <Button
              onClick={() => reopen()}
              loading={isReopening}
              color="violet"
            >
              Reopen
            </Button>
          )
        )}
      </Group>
      <Space h="md" />
      <Group wrap="wrap" justify="center" align="flex-start">
        {request.prodsiteFieldValues.map((psfv) => (
          <Box key={`${request.id}-${psfv.id}`} w="250px" p={10}>
            <CustomFieldInputWrapper
              key={`${request.id}-${psfv.id}`}
              ps={request}
              cfd={psfv.prodsiteFieldDefinition}
              cfv={psfv}
              status={
                isSubmitted
                  ? InformationRequestStatus.CLOSED
                  : InformationRequestStatus.OPENED
              }
              confidential={
                !contacts.some((c) => c.user.id === psfv.submittedById) &&
                !isAdmin
              }
              rightSection={
                <Tooltip label={t("prodsiterequest.actions.transfer")}>
                  <ActionIcon
                    style={{
                      borderRadius: "0 50% 50% 0",
                    }}
                    variant="subtle"
                    onClick={() => {
                      psfv && openTransferModal([psfv.id])
                    }}
                    w="fit-content"
                  >
                    <IconNavion hovered={false} />
                  </ActionIcon>
                </Tooltip>
              }
            />
          </Box>
        ))}
      </Group>
    </Card>
  )
}

type CustomFieldInputProps = {
  ps: SupplierResponses.ProdsitefieldRequest
  cfd: SupplierResponses.ProdsitefieldRequest["prodsiteFieldValues"][number]["prodsiteFieldDefinition"]
  cfv:
    | SupplierResponses.ProdsitefieldRequest["prodsiteFieldValues"][number]
    | undefined
  status: string
  rightSection: React.ReactNode
  confidential?: boolean
}

function CustomFieldInputWrapper({
  ps,
  confidential,
  cfd,
  cfv,
  status,
  rightSection,
}: CustomFieldInputProps) {
  const { isAdmin, user } = useAuth()
  const { t } = useTranslation(["traceability"])

  let label = cfd.textContent.TextTranslation.find(
    (l) => l.translatedLanguage === i18n.language.toUpperCase()
  )
  if (!label) {
    label = cfd.textContent.TextTranslation.find(
      (l) => l.translatedLanguage === "EN"
    )
  }
  const isOwner = cfv?.submittedById === user?.id
  return (
    <Stack>
      {isOwner ? (
        <CustomFieldInput
          ps={ps}
          cfd={cfd}
          cfv={cfv}
          status={status}
          rightSection={rightSection}
        />
      ) : (
        <Text size="sm" w="100%" style={{ textAlign: "center" }}>
          {t("operation.notOwner", {
            email:
              !confidential && cfv?.submittedBy?.email
                ? cfv?.submittedBy?.email
                : t("operation.unknown"),
          })}
        </Text>
      )}
      {isAdmin ? (
        <>
          {cfd.requiredByPartners && cfd.requiredByPartners.length > 0 && (
            <Text fz="xs" c="violet">
              Required for:{" "}
              {cfd.requiredByPartners.map((x) => x.name).join(", ")}
            </Text>
          )}
          <Text fz="xs" c="violet">
            Enabled for:{" "}
            {cfd.enabledForOrganizations?.map((x) => x.company.name).join(", ")}
          </Text>
        </>
      ) : null}
    </Stack>
  )
}

export function isProdsiteFieldRequestSubmitted(
  pfr: SupplierResponses.ProdsitefieldRequest,
  userId: string | undefined
) {
  return pfr.prodsiteFieldValues.every(
    (x) =>
      !!x.submittedAt ||
      (x.submittedById && userId && x.submittedById !== userId)
  )
}

export function isProdsiteFieldRequestValid(
  pfr: SupplierResponses.ProdsitefieldRequest,
  userId: string | undefined
) {
  return pfr.prodsiteFieldValues.every(
    (psfv) => fieldIsValid(psfv) || psfv.submittedById !== userId
  )
}

function fieldIsValid(
  cfv: SupplierResponses.ProdsitefieldRequest["prodsiteFieldValues"][number]
) {
  const value = cfv.value

  if (value === "") {
    return false
  }
  const cfd = cfv.prodsiteFieldDefinition
  const enumValues = cfd.enumValuesTranslated.map((x) => x.originalText)
  const parser = getCustomFieldParser(cfd, enumValues)
  return parser.safeParse(value).success
}

function useTranslatedOptions(enumValues: typeTextContent[]) {
  const currentLanguage = useCurrentLanguage()
  return enumValues.map((x) => ({
    value: x.originalText,
    label: getTranslation(x, currentLanguage),
  }))
}

function CustomFieldInput({
  ps,
  cfd,
  cfv,
  status,
  rightSection,
}: CustomFieldInputProps) {
  const { mutate: setProdsiteFieldValue, isLoading } =
    useSetProdsiteFieldValueMutation(ps.id, cfd.id)
  const { isAdmin } = useAuth()

  let label = cfd.textContent.TextTranslation.find(
    (l) => l.translatedLanguage === i18n.language.toUpperCase()
  )
  if (!label) {
    label = cfd.textContent.TextTranslation.find(
      (l) => l.translatedLanguage === "EN"
    )
  }

  const [value, setValue] = useDebouncedStateWithCallback(
    cfv?.value || "",
    (value) => setProdsiteFieldValue(value),
    400
  )

  const disabled = status !== InformationRequestStatus.OPENED && !isAdmin

  const enumValues = cfd.enumValuesTranslated.map((x) => x.originalText)
  const parser = getCustomFieldParser(cfd, enumValues)
  const parsed = parser.safeParse(value)

  const error =
    parsed?.success === false ? parsed.error.errors[0].message : undefined

  const translatedOptions = useTranslatedOptions(cfd.enumValuesTranslated)

  if (cfd.constraint === "SELECT") {
    return (
      <Select
        key={`${ps.id}-${cfd.id}`}
        label={
          <Group>
            <Text>{label?.translation ?? ""}</Text>
            {isLoading ? (
              <Loader size={12} color="gray" variant="dots" />
            ) : null}
          </Group>
        }
        size="xs"
        error={error}
        value={value}
        onChange={(val) => {
          setValue(val || "")
        }}
        disabled={disabled}
        data={translatedOptions}
        rightSection={value ? undefined : rightSection}
        rightSectionPointerEvents="fill"
      />
    )
  } else {
    return (
      <TextInput
        key={`${ps.id}-${cfd.id}`}
        label={
          <Group>
            <Text>{label?.translation ?? ""}</Text>
            {isLoading ? (
              <Loader size={12} color="gray" variant="dots" />
            ) : null}
          </Group>
        }
        size="xs"
        error={error}
        value={value}
        onChange={(e) => {
          setValue(e.target.value)
        }}
        disabled={disabled}
        onBlur={(e) => {
          setValue(e.target.value)
        }}
        rightSection={value ? undefined : rightSection}
        rightSectionPointerEvents="fill"
      />
    )
  }
}
